import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function Trade() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="tr-page">
      {/* Hero */}
      <section className="tr-hero">
        <div className="tr-hero-overlay"></div>
        <div className="tr-hero-content">
          <span className="tr-label">{lang === 'de' ? t('trade.hero.label') : 'FOR DESIGNERS & ARCHITECTS'}</span>
          <h1 className="tr-hero-title" dangerouslySetInnerHTML={{ __html: lang === 'de' ? t('trade.hero.title') : 'The Kaaleen Haus<br />Trade Program' }}></h1>
          <p className="tr-hero-sub">{lang === 'de' ? t('trade.hero.sub') : 'Exclusive wholesale pricing, dedicated project support, and priority access to new collections for interior design professionals.'}</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="tr-benefits">
        <div className="tr-center">
          <span className="tr-label">{lang === 'de' ? t('trade.benefits.label') : 'WHY JOIN'}</span>
          <h2 className="tr-heading">{lang === 'de' ? t('trade.benefits.title') : 'Trade Program Benefits'}</h2>
        </div>
        <div className="tr-benefits-grid">
          {(lang === 'de' ? t('trade.benefits.items') : [
            { icon: '💰', title: 'Wholesale Pricing', desc: 'Exclusive trade discounts of up to 40% on our entire collection.' },
            { icon: '📐', title: 'Custom Sizing', desc: 'Order rugs in any custom dimension. We cut and bind to your exact specifications.' },
            { icon: '🎨', title: 'Custom Colors', desc: 'Match any Pantone shade. Our dye masters can create bespoke color combinations.' },
            { icon: '🚚', title: 'White-Glove Delivery', desc: 'Priority shipping with careful handling and room-of-choice delivery.' },
            { icon: '👤', title: 'Dedicated Account Manager', desc: 'A personal contact for orders, samples, and project consultations.' },
            { icon: '📦', title: 'Memo Samples', desc: 'Borrow full-size rugs for client presentations. Free shipping both ways.' },
          ]).map((b, i) => (
            <div key={i} className="tr-benefit-card">
              <span className="tr-benefit-icon">{b.icon}</span>
              <h3 className="tr-benefit-title">{b.title}</h3>
              <p className="tr-benefit-desc">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="tr-how">
        <div className="tr-center">
          <span className="tr-label" style={{ color: '#f7bd48' }}>{lang === 'de' ? t('trade.process.label') : 'HOW IT WORKS'}</span>
          <h2 className="tr-heading" style={{ color: '#fff' }}>{lang === 'de' ? t('trade.process.title') : 'Three Simple Steps'}</h2>
        </div>
        <div className="tr-steps">
          {(lang === 'de' ? t('trade.process.steps') : [
            { num: '01', title: 'Apply', desc: 'Fill out the trade application with your business details.' },
            { num: '02', title: 'Verify', desc: 'Our team reviews your application within 24-48 hours.' },
            { num: '03', title: 'Access', desc: 'Get your trade account with exclusive pricing and benefits.' },
          ]).map((s, i) => (
            <div key={i} className="tr-step">
              <span className="tr-step-num">{s.num}</span>
              <h3 className="tr-step-title">{s.title}</h3>
              <p className="tr-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application Form */}
      <section className="tr-apply">
        <div className="tr-apply-inner">
          <div className="tr-apply-text">
            <span className="tr-label">{lang === 'de' ? t('trade.form.label') : 'GET STARTED'}</span>
            <h2 className="tr-heading">{lang === 'de' ? t('trade.form.title') : 'Apply for Trade Access'}</h2>
            <p className="tr-desc">{lang === 'de' ? t('trade.form.desc') : "Tell us about your business and we'll get you set up with a trade account within 48 hours."}</p>
            <div className="tr-contact-info">
              <p>📧 orders@kaaleenhaus.com</p>
              <p>📞 +49 15567406041</p>
            </div>
          </div>
          <div className="tr-apply-form">
            {submitted ? (
              <div className="tr-success">
                <span style={{ fontSize: '48px' }}>✓</span>
                <h3>{lang === 'de' ? t('trade.form.successTitle') : 'Application Submitted!'}</h3>
                <p>{lang === 'de' ? t('trade.form.successDesc') : "We'll review your application and get back to you within 48 hours."}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <input type="text" placeholder={lang === 'de' ? t('trade.form.name') : 'Full Name *'} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                <input type="text" placeholder={lang === 'de' ? t('trade.form.company') : 'Company Name *'} required value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
                <input type="email" placeholder={lang === 'de' ? t('trade.form.email') : 'Email Address *'} required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                <input type="tel" placeholder={lang === 'de' ? t('trade.form.phone') : 'Phone Number'} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                <textarea placeholder={lang === 'de' ? t('trade.form.message') : 'Tell us about your projects...'} rows="4" value={form.message} onChange={e => setForm({...form, message: e.target.value})}></textarea>
                <button type="submit" className="tr-submit">{lang === 'de' ? t('trade.form.submit') : 'Submit Application'}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{tradeStyles}</style>
    </div>
  );
}

const tradeStyles = `
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap');
.tr-page { background: #fbfaee; }
.tr-label { display: block; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: #785600; margin-bottom: 14px; }
.tr-heading { font-family: 'Noto Serif', Georgia, serif; font-size: 2.4rem; font-weight: 400; color: #1b1c15; margin: 0 0 20px; }
.tr-desc { font-family: 'Inter', sans-serif; font-size: 15px; color: #4f4535; line-height: 1.85; }
.tr-center { text-align: center; margin-bottom: 50px; }

/* Hero */
.tr-hero { position: relative; min-height: 50vh; display: flex; align-items: center; justify-content: center; text-align: center; background: linear-gradient(135deg, #1b1c15, #2d2416, #1b1c15); overflow: hidden; }
.tr-hero-overlay { position: absolute; inset: 0; background: radial-gradient(ellipse at center, rgba(120,86,0,0.15), transparent 70%); }
.tr-hero-content { position: relative; z-index: 2; max-width: 700px; padding: 60px 20px; }
.tr-hero-content .tr-label { color: #f7bd48; }
.tr-hero-title { font-family: 'Noto Serif', Georgia, serif; font-size: 3.5rem; font-weight: 400; color: #fff; margin: 0 0 20px; line-height: 1.1; }
.tr-hero-sub { font-family: 'Inter', sans-serif; color: rgba(255,255,255,0.65); font-size: 15px; line-height: 1.8; }

/* Benefits */
.tr-benefits { padding: 80px 60px; }
.tr-benefits-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; max-width: 1100px; margin: 0 auto; }
.tr-benefit-card { background: #f5f4e8; padding: 36px 28px; text-align: center; border-radius: 4px; }
.tr-benefit-icon { font-size: 32px; display: block; margin-bottom: 14px; }
.tr-benefit-title { font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 600; color: #1b1c15; margin: 0 0 10px; }
.tr-benefit-desc { font-family: 'Inter', sans-serif; font-size: 13px; color: #4f4535; line-height: 1.7; margin: 0; }

/* How */
.tr-how { padding: 80px 60px; background: #1b1c15; }
.tr-steps { display: flex; gap: 40px; max-width: 900px; margin: 0 auto; justify-content: center; }
.tr-step { text-align: center; flex: 1; }
.tr-step-num { font-family: 'Noto Serif', Georgia, serif; font-size: 3rem; font-weight: 400; color: #f7bd48; display: block; margin-bottom: 12px; }
.tr-step-title { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 600; color: #fff; margin: 0 0 8px; }
.tr-step-desc { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.7; margin: 0; }

/* Apply */
.tr-apply { padding: 80px 60px; background: #f5f4e8; }
.tr-apply-inner { display: flex; gap: 60px; max-width: 1100px; margin: 0 auto; }
.tr-apply-text { flex: 1; padding-top: 20px; }
.tr-contact-info { margin-top: 30px; }
.tr-contact-info p { font-family: 'Inter', sans-serif; font-size: 14px; color: #4f4535; margin: 0 0 8px; }
.tr-apply-form { flex: 1; background: #fff; padding: 40px; border-radius: 4px; }
.tr-apply-form input, .tr-apply-form textarea { width: 100%; padding: 14px 16px; border: none; border-bottom: 1.5px solid #d3c4af; background: transparent; font-family: 'Inter', sans-serif; font-size: 14px; margin-bottom: 20px; outline: none; transition: border-color 0.3s; color: #1b1c15; box-sizing: border-box; }
.tr-apply-form input:focus, .tr-apply-form textarea:focus { border-bottom-color: #785600; }
.tr-apply-form textarea { resize: vertical; }
.tr-submit { width: 100%; padding: 16px; background: linear-gradient(135deg, #785600, #986d00); color: #fff; border: none; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; border-radius: 4px; transition: opacity 0.3s; }
.tr-submit:hover { opacity: 0.9; }
.tr-success { text-align: center; padding: 40px 20px; color: #785600; }
.tr-success h3 { font-family: 'Noto Serif', Georgia, serif; font-size: 1.5rem; margin: 16px 0 8px; color: #1b1c15; }
.tr-success p { font-size: 14px; color: #4f4535; }

@media (max-width: 900px) {
  .tr-benefits-grid { grid-template-columns: repeat(2, 1fr); }
  .tr-steps { flex-direction: column; gap: 30px; }
  .tr-apply-inner { flex-direction: column; }
  .tr-hero-title { font-size: 2.4rem; }
  .tr-benefits, .tr-how, .tr-apply { padding: 50px 20px; }
}
@media (max-width: 480px) { .tr-benefits-grid { grid-template-columns: 1fr; } }
`;
